// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { fetchVisitCount } from '../../utils/visitTracking';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import TurtleIcon from '../AssistantWidget/TurtleIcon';
import styles from './footer.module.scss';
import packageJson from '../../../package.json';
import { useLocalizedPath } from '../../i18n/localizedPath';
import LanguageSwitcher from '../header/LanguageSwitcher';

// Same breakpoint footer.module.scss already switches to the stacked,
// centered mobile layout at - below it, the full link list is too much
// text for a footer and folds behind a toggle instead.
const MOBILE_FOOTER_QUERY = '(max-width: 576px)';

function openCookieSettings(event: React.MouseEvent<HTMLAnchorElement>) {
    const consentWindow = window as Window & {
        _iub?: { cs?: { api?: { openPreferences?: () => void } } };
    };
    const api = consentWindow._iub?.cs?.api;
    if (typeof api?.openPreferences === "function") {
        event.preventDefault();
        api.openPreferences();
    }
}

const FooterComponent = () => {
    const { t } = useTranslation();
    const lp = useLocalizedPath();
    const currentYear = new Date().getFullYear();
    const version = `v${packageJson.version}`;
    const [siteVisits, setSiteVisits] = useState(null);
    const isMobileFooter = useMediaQuery(MOBILE_FOOTER_QUERY);
    const [isLinksOpen, setIsLinksOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsLinksOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        let cancelled = false;

        fetchVisitCount()
            .then((visits) => {
                if (!cancelled && visits !== null) {
                    setSiteVisits(visits);
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <footer className={styles.container}>
            <div className={styles.footerContent}>
                <div className={styles.topRow}>
                    <div className={styles.brandSection}>
                        <div className={styles.brandRow}>
                            <span className={styles.brandMark}><TurtleIcon size={32} animated={false} /></span>
                            <span className={styles.brand}>NihongoTech</span>
                            <span className={styles.version}>{version}</span>
                            {siteVisits !== null && (
                                <span className={styles.visitCount} title={t("footer.siteVisits")}>
                                    <FontAwesomeIcon icon={faEye} className={styles.visitCountIcon} />
                                    {siteVisits.toLocaleString()}
                                </span>
                            )}
                        </div>
                        <span className={styles.tagline}>{t("footer.tagline")}</span>
                    </div>

                    <div className={styles.linksSection}>
                    {isMobileFooter && (
                        <button
                            type="button"
                            className={styles.linksToggle}
                            onClick={() => setIsLinksOpen((open) => !open)}
                            aria-expanded={isLinksOpen}
                            aria-controls="footer-links"
                        >
                            {isLinksOpen ? t('footer.showLess') : t('footer.showMore')}
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={isLinksOpen ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
                            />
                        </button>
                    )}
                    {(!isMobileFooter || isLinksOpen) && (
                    <nav id="footer-links" className={styles.linksRow} aria-label={t("footer.navLabel")}>
                        <Link to={lp("/offline")} className={styles.footerLink}>
                            {t("footer.nav.offline")}
                        </Link>
                        <Link to={lp("/")} className={styles.footerLink}>
                            {t('footer.nav.home')}
                        </Link>

                        <Link to={lp("/lessons")} className={styles.footerLink}>
                            {t('footer.nav.learn')}
                        </Link>

                        <Link to={lp("/#practice")} className={styles.footerLink}>
                            {t('footer.nav.practice')}
                        </Link>

                        <Link to={lp("/keyboard")} className={styles.footerLink}>
                            {t('footer.nav.keyboard')}
                        </Link>

                        <Link to={lp("/grammar")} className={styles.footerLink}>
                            {t('footer.nav.grammar')}
                        </Link>

                        <Link to={lp("/vocabulary")} className={styles.footerLink}>
                            {t('footer.nav.vocabulary')}
                        </Link>

                        <Link to={lp("/conjugation")} className={styles.footerLink}>
                            {t('footer.nav.conjugation')}
                        </Link>

                        <Link to={lp("/wanikani")} className={styles.footerLink}>
                            {t('footer.nav.wanikani')}
                        </Link>

                        <Link to={lp("/converter")} className={styles.footerLink}>
                            {t('footer.nav.hiraganaConverter')}
                        </Link>
                        <Link to={lp("/romaji-converter")} className={styles.footerLink}>
                            {t('footer.nav.romajiConverter')}
                        </Link>
                        <Link to={lp("/blog")} className={styles.footerLink}>
                            {t('footer.nav.blog')}
                        </Link>
                        <Link to={lp("/about")} className={styles.footerLink}>
                            {t('footer.nav.about')}
                        </Link>
                        <Link to={lp("/study-materials")} className={styles.footerLink}>
                            {t('footer.nav.studyMaterials')}
                        </Link>
                        <Link to={lp("/releases")} className={styles.footerLink}>
                            {t('footer.nav.releaseNotes')}
                        </Link>

                        <Link to={lp("/newsletter")} className={styles.footerLink}>
                            {t('footer.nav.newsletter')}
                        </Link>

                        <Link to={lp("/privacy-policy")} className={styles.footerLink}>
                            {t('footer.nav.privacyPolicy')}
                        </Link>

                        <LanguageSwitcher variant="footer" className={styles.footerLink} />

                        <a
                            href="https://www.iubenda.com/privacy-policy/46335044/cookie-policy"
                            className={styles.footerLink}
                            onClick={openCookieSettings}
                        >
                            {t('footer.nav.cookieSettings')}
                        </a>

                        <a
                            href="mailto:admin@teqseq.com?subject=Kana%20Learn%20bug%20report"
                            className={styles.footerLink}
                        >
                            {t('footer.nav.reportBug')}
                        </a>
                    </nav>
                    )}
                    </div>
                </div>
                <div className={styles.metaRow}>
                    <span className={styles.madeWith}>
                        {t('footer.madeWithPrefix')}{' '}
                        <a
                            href="https://teqseq.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.brandLink}
                        >
                            {t('footer.madeWithBrand')}
                        </a>
                    </span>
                    <span className={styles.copyright}>{t('footer.copyright', { year: currentYear })}</span>
                </div>
            </div>
        </footer>
    );
};

export default FooterComponent;
